import { TableTextOverflow } from '@/components/TableTextOverflow/TableTextOverflow';
import { TransactionProperty } from '@/features/InvoiceReport/Edit/type';
import { InvoiceTransactionDefaultColumn } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/TransactionsDetail/Table/Columns/columns';
import { InvoiceTransactionColumn } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/TransactionsDetail/hooks/useInvoiceTransactionColumns';
import { toCommaSeparated } from '@/utils/convert/toCommaSeparated';
import { Skeleton } from '@moneyforward/ap-frontend-components';
import type { InvoiceTransactionDetail } from 'ap-openapi';
import { DATE_FORMAT, DATE_MONTH_FORMAT, format } from 'date-util';
import { TFunction } from 'i18next';

//Check if the custom transaction key exists in the invoice transaction data
export const isCustomTransactionKeyExists = (
  data: InvoiceTransactionDetail[]
): Set<string> => {
  const keySet = new Set<string>();

  data.forEach((item) => {
    const customItems = item.custom_transaction_items;
    if (!customItems) return;

    Object.entries(customItems).forEach(([key]) => {
      keySet.add(key);
    });
  });

  return keySet;
};

export const getDefaultColumnProps = (
  defaultColumns: InvoiceTransactionDefaultColumn[],
  masterFieldName: string | undefined
): InvoiceTransactionDefaultColumn | undefined =>
  masterFieldName
    ? defaultColumns.find((item) => item.masterFieldName === masterFieldName)
    : undefined;

const getDisplayedValue = (
  dynamicColumnProps: TransactionProperty,
  value: string | undefined
) => {
  switch (dynamicColumnProps.componentType) {
    case 'numeric':
      return value ? toCommaSeparated(value) : '';
    case 'date':
      return format(
        value ?? '',
        dynamicColumnProps.dateFormatType === 'year_month_day'
          ? DATE_FORMAT
          : DATE_MONTH_FORMAT
      );
    default:
      return value ?? '';
  }
};

export const getDynamicColumnProps = (
  defaultColumns: InvoiceTransactionDefaultColumn[],
  dynamicColumnProps: TransactionProperty,
  t: TFunction,
  isLastColumn: boolean = false,
  pulldownLabelsMap: Map<string, Map<string, string>>,
  isLoadingPulldownLabels: boolean
): InvoiceTransactionColumn | undefined => {
  const title = dynamicColumnProps.label;
  const key = dynamicColumnProps.id;

  const masterFieldName = dynamicColumnProps.masterFieldName;
  const defaultProps = getDefaultColumnProps(defaultColumns, masterFieldName);

  if (defaultProps) {
    const align = defaultProps.align;
    const dataIndex = defaultProps.dataIndex;
    const render = defaultProps.render;
    const width = defaultProps.width;

    return {
      title,
      dataIndex,
      render,
      key,
      align,
      width,
    };
  } else {
    return {
      title: () => {
        return <TableTextOverflow title={title} />;
      },
      cardLabel: title,
      key,
      width: 160,
      align:
        dynamicColumnProps.componentType === 'numeric'
          ? ('right' as const)
          : ('left' as const),
      dataIndex: ['custom_transaction_items', key],
      render(value: string | undefined) {
        if (dynamicColumnProps.componentType === 'pulldown') {
          if (isLoadingPulldownLabels) {
            return <Skeleton.Input active size='small' block />;
          }
          const pulldownLabel = pulldownLabelsMap.get(key)?.get(value ?? '');
          return (
            <>{pulldownLabel && <TableTextOverflow title={pulldownLabel} />}</>
          );
        }
        const displayedValue = getDisplayedValue(dynamicColumnProps, value);
        return (
          <TableTextOverflow
            title={displayedValue}
            isLastColumn={isLastColumn}
            isTextAlignEnd={dynamicColumnProps.componentType === 'numeric'}
          />
        );
      },
    };
  }
};
